// @ts-check

import { hasOwnProperty } from '../utils/utils';

/**
 * @param {HTMLElement} element
 * @param {HTMLChild} child
 * @returns {HTMLElement}
 */
function appendChild(element, child) {
  if (typeof child === 'string') {
    element.insertAdjacentHTML('beforeend', child);
  } else {
    element.appendChild(child);
  }

  return element;
}

/**
 * @param {HTMLElement} element
 * @param {HTMLChildren} [children]
 * @returns {HTMLElement}
 */
function appendChildren(element, children) {
  if (!children) {
    return element;
  }

  if (Array.isArray(children)) {
    children
      .filter(Boolean) // Support arrays like [addChild ? child1 : null, child2]
      .forEach((child) => {
        appendChild(element, child);
      });
  } else {
    appendChild(element, children);
  }

  return element;
}

const BOOLEAN_ATTRIBUTES = ['checked', 'disabled'];

/**
 * @param {HTMLElement} element
 * @param {HTMLAttributesMap} attributes
 * @returns {HTMLElement}
 */
function setAttributes(element, attributes) {
  Object.entries(attributes).forEach(([name, value]) => {
    // Boolean attributes must be set in a specified way
    if (BOOLEAN_ATTRIBUTES.includes(name)) {
      if (value) {
        element.setAttribute(name, '');
      }
    } else {
      element.setAttribute(name, value);
    }
  });

  return element;
}

/**
 * @param {HTMLElement} element
 * @param {DOMStringMap} dataset
 * @returns {HTMLElement}
 */
function setDataset(element, dataset) {
  Object.entries(dataset).forEach(([key, value]) => {
    element.dataset[key] = value;
  });

  return element;
}

/**
 * @param {HTMLElement} element
 * @param {string[]} classList
 * @returns {HTMLElement}
 */
function setClassList(element, classList) {
  classList
    .filter(Boolean)
    .forEach((className) => {
      element.classList.add(className);
    });

  return element;
}

/**
 * @param {HTMLElement} element
 * @param {EventListenersMap} eventsMap
 * @returns {HTMLElement}
 */
function addEventListeners(element, eventsMap) {
  Object.entries(eventsMap).forEach(([event, params]) => {
    let listener;
    let options;

    if (Array.isArray(params)) {
      [listener, options] = params;
    } else {
      listener = params;
    }

    element.addEventListener(event, listener, options);
  });

  return element;
}

const SPECIAL_OPTIONS = ['contents', 'attributes', 'dataset', 'classList', 'events'];

/**
 * @param {HTMLElement} element
 * @param {HTMLElementOptions} options
 * @returns {HTMLElement}
 */
function applyHtmlOptions(element, options) {
  if (!options) {
    return element;
  }

  Object.entries(options)
    .filter(([key]) => !SPECIAL_OPTIONS.includes(key))
    .forEach(([key, value]) => { element[key] = value; });

  // Apply special options afterwards for predictability sake

  if (hasOwnProperty(options, 'attributes')) {
    setAttributes(element, options.attributes);
  }

  if (hasOwnProperty(options, 'contents')) {
    appendChildren(element, options.contents);
  }

  if (hasOwnProperty(options, 'dataset')) {
    setDataset(element, options.dataset);
  }

  if (hasOwnProperty(options, 'classList')) {
    setClassList(element, options.classList);
  }

  if (hasOwnProperty(options, 'events')) {
    addEventListeners(element, options.events);
  }

  return element;
}

/**
 * @typedef {object} HTMLElementOptions
 * @property {HTMLChildren} [contents] Falsy values are removed.
 * @property {HTMLAttributesMap} [attributes]
 * @property {DOMStringMap} [dataset]
 * @property {string[]} [classList]
 * @property {EventListenersMap} [events]
 * Element properties.
 * @property {string} [id]
 * @property {string} [className]
 * @property {string} [innerHTML]
 * HTMLElement properties:
 * @property {string} [innerText]
 * @property {string} [title]
 * HTMLButtonElement properties:
 * @property {string} [type] Button/inputs type
 * HTMLInputElement text/number properties:
 * @property {string} [placeholder]
 * @property {number} [maxLength]
 * HTMLInputElement checkbox properties:
 * @property {boolean} [checked]
 * HTMLLinkElement properties:
 * @property {string} [href]
 * @property {string} [target]
 * HTMLImageElement:
 * @property {string} [alt]
 * @property {number} [width]
 * @property {number} [height]
 * @property {string} [src]
 */

/**
 * @typedef {Node | string} HTMLChild
 * @typedef {HTMLChild | HTMLChild[]} HTMLChildren
 */

/**
 * @typedef { { [name: string]: * } } HTMLAttributesMap
 */

/**
 * @typedef { { [name: string]: ((e: Event) => void) | [(e: Event) => void, boolean | AddEventListenerOptions]) } } EventListenersMap Event name callback map
 * @example
 * {
 *   'click': (e) => {},
 *   'touchmove': [(e) => {}, false]
 *   'mouseleave': [(e) => {}, { once: true }]
 * }
 */

/**
 * Syntax sugar wrapper around `document.createElement` with possibility to set attributes, children, classList, dataset
 * and event listeners.
 *
 * @param {string} [tag]
 * @param {HTMLElementOptions} options
 * @returns {HTMLElement}
 */
export function createElement(tag, options) {
  const element = document.createElement(tag);

  return applyHtmlOptions(element, options);
}

/**
 * Create SVG icon element.
 *
 * @param {object} iconObject Object with icon properties.
 *   @param {string} iconObject.name Icon name used in sprite.
 *   @param {number} iconObject.width Icon width.
 *   @param {number} iconObject.height Icon height.
 *   @param {boolean} [iconObject.isForButton] If `true` removes `<figure>` wrapper.
 *   @param {object} [iconObject.viewport] Icon viewport properties.
 *   @param {number} iconObject.viewport.width Icon viewport width.
 *   @param {number} iconObject.viewport.height Icon viewport height.
 *   @param {string} [iconObject.classList=''] Icon classes separated by the space symbol.
 * @returns {SVGSVGElement | HTMLElement} SVG icon element or SVG icon element wrapped with figure element.
 */
export function createSVGIcon({ name, width, height, viewport, classList = '', isForButton }) {
  const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');

  // TODO: Attach `pes-icon-` to `name` after all icons moved to sprite
  use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', `#${name}`);

  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

  applyHtmlOptions(icon, {
    attributes: {
      width,
      height,
      viewBox: `0 0 ${viewport ? viewport.width : width} ${viewport ? viewport.height : height}`,
      class: `icon${classList && isForButton ? ` ${classList}` : ''}`
    },
    contents: use
  });

  if (isForButton) {
    return icon;
  }

  return createElement('figure', {
    className: `figure-icon ${classList}`,
    contents: icon
  });
}

/**
 * Create object which contains html string for use it in dangerouslySetInnerHTML.
 *
 * @param {string} rawHtml Html string.
 * @returns {{ __html: string}}
 */
export const createInnerHtml = (rawHtml) => ({ __html: rawHtml });
